import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TransactionOverlay from '../../components/transactions/TransactionOverlay';
import PurchasesManage, { type PurchasesManageSaveReason } from '../PurchasesManage';
import type { Bill } from '../../types/bill';
import { useInventoryTransactionsHub } from './InventoryTransactionsHubContext';
import {
  parseInventoryHubLocationState,
  resolveBillEditCloseTarget,
} from './inventoryHubNavigation';

const InventoryBillEditOverlay = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchList } = useInventoryTransactionsHub();
  const listSearch = location.search.replace(/^\?/, '');
  const navState = useMemo(
    () => parseInventoryHubLocationState(location.state),
    [location.state]
  );

  const isNew = id === 'new';
  const parsedId = id != null ? Number(id) : Number.NaN;
  const billId = isNew || Number.isNaN(parsedId) || parsedId <= 0 ? undefined : parsedId;

  const close = useCallback(() => {
    const target = resolveBillEditCloseTarget(billId, listSearch, navState);
    navigate(target, { preventScrollReset: true });
  }, [billId, listSearch, navState, navigate]);

  const handleBillSaved = useCallback(
    (bill: Bill, reason: PurchasesManageSaveReason) => {
      refetchList();

      if (reason === 'draft_created') {
        navigate(
          {
            pathname: `/inventory-transactions/bill/${bill.id}`,
            search: listSearch,
          },
          { replace: true, preventScrollReset: true, state: location.state }
        );
        return;
      }

      if (
        reason === 'received' ||
        reason === 'amended' ||
        reason === 'cost_corrected' ||
        reason === 'lines_appended'
      ) {
        navigate(
          {
            pathname: `/inventory-transactions/view/bill/${bill.id}`,
            search: listSearch,
          },
          { replace: true, preventScrollReset: true, state: location.state }
        );
      }
    },
    [close, listSearch, location.state, navigate, refetchList]
  );

  return (
    <TransactionOverlay headerless variant="wide" onClose={close}>
      <PurchasesManage
        key={billId ?? 'new'}
        embedded
        billId={billId}
        onClose={close}
        onBillSaved={handleBillSaved}
      />
    </TransactionOverlay>
  );
};

export default InventoryBillEditOverlay;
